import usePlans from 'shared/services/plans';
import { computed, ref, watch } from 'vue';

import { paddleSubscriptionActions, paddleSubscriptionTrials } from '../constants';

export default function useSubscription(user, betaPhaseManager) {
  const { plans } = usePlans(betaPhaseManager);

  const currentPlan = computed(() => plans.value[user.value.subscriptionData.plan]);

  const currentSubscriptionData = computed(() => user.value.subscriptionData);

  const isHighlighted = (plan) => {
    if (currentSubscriptionData.value.cancelled && plan.id === plans.value.basic.id) {
      return true;
    }

    if (plan.id === currentPlan.value.id) {
      return false;
    }

    if (plan.id === plans.value.basic.id) {
      return true;
    }
    return false;
  };

  const getPriceIdAndAction = (plan) => {
    if (currentSubscriptionData.value.cancelled) {
      if (plan.id === plans.value.free.id) {
        return { action: null, priceId: null };
      }
      return { action: paddleSubscriptionActions.reactivate, priceId: null };
    }

    if (currentPlan.value.id === plan.id) {
      return { action: null, priceId: null };
    }

    if (plan.id === plans.value.free.id) {
      return { action: paddleSubscriptionActions.cancel, priceId: null };
    }

    const trialValid = currentSubscriptionData.value.trialTaken ? paddleSubscriptionTrials.noTrial : paddleSubscriptionTrials.trial;

    return { action: paddleSubscriptionActions.checkout, priceId: plan.prices[trialValid] };
  };

  const getCta = (plan) => {
    const res = { disabled: false, isHighlighted: isHighlighted(plan), ...getPriceIdAndAction(plan) };

    if (currentSubscriptionData.value.cancelled) {
      if (plan.id === plans.value.basic.id) {
        return { ...res, text: 'Reactivate' };
      }
      return { ...res, text: 'Downgrade', disabled: true };
    }

    if (plan.id === currentPlan.value.id) {
      return { ...res, text: 'Current plan', disabled: true };
    }

    if (betaPhaseManager.isInBeta.value) {
      return { ...res, text: 'Contact us' };
    }

    if (!currentSubscriptionData.value.trialTaken) {
      // trialTaken is False only if currentPlan is free and a subscription has never been taken.
      return { ...res, text: 'Try for free' };
    }

    if (plan.id === plans.value.basic.id) {
      return { ...res, text: 'Upgrade' };
    }
    return { ...res, text: 'Downgrade' };
  };

  const addCTA = (plan) => ({
    ...plan,
    cta: getCta(plan),
  });

  const hasStorage = computed(() => {
    if (currentSubscriptionData.value.pastDue) {
      return plans.value.free.storageLimit > user.value.subscriptionData.storageUsed;
    }
    return currentPlan.value.storageLimit > user.value.subscriptionData.storageUsed;
  });

  const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  const renewsAt = computed(() => {
    if (currentSubscriptionData.value.nextBilling) {
      return new Date(currentSubscriptionData.value.nextBilling).toLocaleDateString('en-US', dateOptions);
    }
    return '';
  });

  const loadingSubscriptionData = ref(false);
  watch(currentSubscriptionData, (newValue, oldValue) => {
    loadingSubscriptionData.value = false;
  }, { deep: true });

  return {
    isTrialing: computed(() => currentSubscriptionData.value.isTrialing),
    cancelled: computed(() => currentSubscriptionData.value.cancelled),
    hasStorage,
    currentPlan,
    renewsAt,
    subscriptionPastDue: computed(() => currentSubscriptionData.value.pastDue),
    loadingSubscriptionData,
    plans: computed(() => Object.keys(plans.value).map((key) => addCTA(plans.value[key]))),
  };
}
